import React, { useEffect, useState } from 'react';
import {
  Typography,
  Breadcrumb,
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  InputNumber,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Paragraph } = Typography;

const WorkspacesOverview = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const API_BASE_URL = '/api/workspaces';

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
    { title: 'Workspace', dataIndex: 'name', key: 'name' },
    { title: 'Agency ID', dataIndex: 'agency_id', key: 'agency_id', width: 120 },
    { title: 'Client ID', dataIndex: 'client_id', key: 'client_id', width: 120 },
    {
      title: 'Paid',
      dataIndex: 'is_paid',
      key: 'is_paid',
      width: 100,
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 180, render: (d) => new Date(d).toLocaleString() },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this workspace?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(record.id)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    }
  ];

  const fetchWorkspaces = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset: (page - 1) * pageSize });
      const { data: res } = await axios.get(`${API_BASE_URL}?${params.toString()}`);
      setData(res.items || []);
      setPagination({ current: page, pageSize, total: res.count ?? (res.items?.length || 0) });
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = (pag) => {
    fetchWorkspaces(pag.current, pag.pageSize);
  };

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const onEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      agency_id: record.agency_id,
      client_id: record.client_id,
      is_paid: record.is_paid,
    });
    setIsModalVisible(true);
  };

  const authHeader = () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/${id}`, { headers: authHeader() });
      message.success('Workspace deleted');
      fetchWorkspaces(pagination.current, pagination.pageSize);
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        agency_id: values.agency_id ?? null,
        client_id: values.client_id ?? null,
        is_paid: !!values.is_paid,
      };

      if (editingId) {
        await axios.put(`${API_BASE_URL}/${editingId}`, payload, { headers: authHeader() });
        message.success('Workspace updated');
      } else {
        await axios.post(`${API_BASE_URL}`, payload, { headers: authHeader() });
        message.success('Workspace created');
      }
      setIsModalVisible(false);
      fetchWorkspaces(pagination.current, pagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error(e.response?.data?.error || 'Save failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Workspaces' },
        { title: 'Overview' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Workspaces Overview</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Workspace</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage workspaces. Create, edit, or delete entries.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={onTableChange}
          />
      </Card>

      <Modal
        title={editingId ? 'Edit Workspace' : 'New Workspace'}
        open={isModalVisible}
        onOk={onSubmit}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Workspace name" />
          </Form.Item>
          <Form.Item name="agency_id" label="Agency ID">
            <InputNumber style={{ width: '100%' }} placeholder="Agency ID (optional)" />
          </Form.Item>
          <Form.Item name="client_id" label="Client ID">
            <InputNumber style={{ width: '100%' }} placeholder="Client ID (optional)" />
          </Form.Item>
          <Form.Item name="is_paid" label="Paid" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkspacesOverview;
